import { useCallback, useEffect, useRef, useState } from 'react'
import { referencedAssetIds, remapSnapshot } from '../utils/templateSnapshots'

/**
 * Whether `next` only fills in what the editor derives by itself: a rendition made for a picture
 * that had none yet (the crop preview after a pick). Such a change belongs to the step that
 * caused it; recording it alone would make Undo land on a half-made picture whose preview the
 * field makes again at once, and that new change would clear Redo.
 */
export function onlyFillsRenditions(previous, next) {
  if (previous === next) return true
  if (Array.isArray(previous) || Array.isArray(next)) {
    if (!Array.isArray(previous) || !Array.isArray(next) || previous.length !== next.length) return false
    return previous.every((entry, index) => onlyFillsRenditions(entry, next[index]))
  }
  if (previous && next && typeof previous === 'object' && typeof next === 'object') {
    const filled = Object.prototype.hasOwnProperty.call(next, 'sourceAssetId')
      && previous.sourceAssetId === next.sourceAssetId && !previous.renditionAssetId && Boolean(next.renditionAssetId)
    for (const key of new Set([...Object.keys(previous), ...Object.keys(next)])) {
      if (filled && (key === 'renditionAssetId' || key === 'crop')) continue
      if (!onlyFillsRenditions(previous[key], next[key])) return false
    }
    return true
  }
  return false
}

/**
 * AD-234: Undo / Redo of the Edit Ad step.
 *
 * Watches the ad (template and configuration) rather than wrapping every action, so texts,
 * pictures, slides, design, template switches and Campaign Setting are all covered. One step per
 * settled edit (typing in a field is one step), at most `limit`. A step restores the template,
 * the configuration and the slide/item selection; picture previews are never taken back, so an
 * undone picture still shows. The editor starts a new history on reopening, after an AI
 * generation, "Build without AI" and a template upgrade (`reset()`), and points the steps at the
 * stored files after a save (`remap()`).
 */
export function useEditHistory({
  template, configuration, assetUrls, assetSourceUrls, assetSrcSets, selectedSlide, selectedItems, apply,
  limit = 50, settleMs = 500,
}) {
  const past = useRef([])
  const future = useRef([])
  const current = useRef(null)
  const runBase = useRef(null)
  const timer = useRef(null)
  const applying = useRef(false)
  const amending = useRef(false)
  const resetting = useRef(false)
  // Every preview URL seen in this history: a step may show a picture the current maps dropped.
  const knownUrls = useRef({ assetUrls: {}, assetSourceUrls: {}, assetSrcSets: {} })
  const selection = useRef({ selectedSlide, selectedItems })
  selection.current = { selectedSlide, selectedItems }

  const [, bump] = useState(0)
  const rerender = useCallback(() => bump((n) => n + 1), [])

  useEffect(() => () => clearTimeout(timer.current), [])

  useEffect(() => {
    knownUrls.current = {
      assetUrls: { ...knownUrls.current.assetUrls, ...(assetUrls || {}) },
      assetSourceUrls: { ...knownUrls.current.assetSourceUrls, ...(assetSourceUrls || {}) },
      assetSrcSets: { ...knownUrls.current.assetSrcSets, ...(assetSrcSets || {}) },
    }
  }, [assetSrcSets, assetSourceUrls, assetUrls])

  const settle = useCallback(() => {
    clearTimeout(timer.current)
    if (!runBase.current) return
    past.current = [...past.current, runBase.current].slice(-limit)
    future.current = []
    runBase.current = null
    rerender()
  }, [limit, rerender])

  useEffect(() => {
    if (!template || !configuration) return
    const previous = current.current
    const entry = { template, configuration, ...selection.current }
    current.current = entry
    if (!previous || resetting.current) {
      resetting.current = false
      amending.current = false
      clearTimeout(timer.current)
      past.current = []
      future.current = []
      runBase.current = null
      rerender()
      return
    }
    if (applying.current) {
      applying.current = false
      return
    }
    if (previous.template === template && previous.configuration === configuration) return
    if (amending.current || (previous.template === template && onlyFillsRenditions(previous.configuration, configuration))) {
      // Derived for the current step: it becomes part of it, and Redo stays.
      amending.current = false
      return
    }
    if (!runBase.current) {
      runBase.current = previous
      rerender()
    }
    clearTimeout(timer.current)
    timer.current = setTimeout(settle, settleMs)
  }, [configuration, template]) // eslint-disable-line react-hooks/exhaustive-deps

  const restore = useCallback((entry) => {
    applying.current = true
    apply(entry, knownUrls.current)
    rerender()
  }, [apply, rerender])

  const undo = useCallback(() => {
    settle()
    if (!past.current.length || !current.current) return
    const previous = past.current[past.current.length - 1]
    past.current = past.current.slice(0, -1)
    future.current = [...future.current, current.current]
    restore(previous)
  }, [restore, settle])

  const redo = useCallback(() => {
    settle()
    if (!future.current.length || !current.current) return
    const next = future.current[future.current.length - 1]
    future.current = future.current.slice(0, -1)
    past.current = [...past.current, current.current]
    restore(next)
  }, [restore, settle])

  /** The next change of the ad starts a new history (call right before setting it). */
  const reset = useCallback(() => { resetting.current = true }, [])

  /** The next change of the ad is derived, not an edit: it amends the current step. */
  const amend = useCallback(() => { amending.current = true }, [])

  /** After a save: the steps point at the stored files instead of the uploads they replaced. */
  const remap = useCallback((mapping, savedConfiguration) => {
    settle()
    if (!Object.keys(mapping || {}).length) return
    const remapped = (entry) => ({
      ...entry,
      configuration: remapSnapshot({ configuration: entry.configuration }, mapping, savedConfiguration).configuration,
    })
    past.current = past.current.map(remapped)
    future.current = future.current.map(remapped)
  }, [settle])

  /** The pictures the steps still show, so a save keeps their pending uploads. */
  const referencedIds = useCallback(() => new Set(
    [...past.current, ...future.current].flatMap((entry) => [...referencedAssetIds(entry.configuration)]),
  ), [])

  return {
    undo,
    redo,
    reset,
    amend,
    remap,
    referencedIds,
    canUndo: past.current.length > 0 || runBase.current !== null,
    canRedo: future.current.length > 0,
  }
}
